/**
 * Enhanced Button Component
 * Premium button with glass effects and animations
 */

import { ComponentProps, forwardRef } from 'react'
import { LoadingIcon } from '../icons'
import clsx from 'clsx'

interface ButtonProps extends ComponentProps<'button'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  children,
  className = '',
  disabled,
  ...props
}, ref) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    font-semibold rounded-xl
    transition-all duration-300
    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    relative overflow-hidden
  `
  
  const variantStyles = {
    primary: `
      bg-gradient-to-br from-[#667EEA] to-[#764BA2]
      text-white
      shadow-[0_4px_12px_rgba(102,126,234,0.4)]
      hover:shadow-[0_8px_24px_rgba(102,126,234,0.5)]
      hover:scale-[1.02]
      active:scale-[0.98]
      focus-visible:ring-[#667EEA]
      before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent
      before:translate-x-[-200%] hover:before:translate-x-[200%]
      before:transition-transform before:duration-500
    `,
    secondary: `
      bg-white/10
      backdrop-blur-md
      border border-white/15
      text-white
      shadow-md
      hover:bg-white/15
      hover:border-white/20
      hover:shadow-lg
      active:scale-[0.98]
      focus-visible:ring-white/50
    `,
    ghost: `
      text-gray-300
      hover:bg-white/5
      hover:text-white
      active:scale-[0.98]
      focus-visible:ring-white/30
    `,
    danger: `
      bg-red-500/20
      border border-red-500/50
      text-red-400
      hover:bg-red-500/30
      hover:border-red-500/70
      active:scale-[0.98]
      focus-visible:ring-red-500
    `,
  }
  
  const sizeStyles = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg',
  }
  
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <>
          <LoadingIcon size="sm" className="animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          {icon && iconPosition === 'left' && icon}
          {children}
          {icon && iconPosition === 'right' && icon}
        </>
      )}
    </button>
  )
})

Button.displayName = 'Button'

export default Button
